import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import {
  AccessStarts,
  Coverage,
  GroupParticipant,
  LicenseAlerts,
  LicenseGroup,
  ProgramFamily,
} from './LicenseAlertsStore/Types';

export const coversEverything = (coverage: Coverage): boolean => {
  switch (coverage) {
    case 'full':
      return true;
    case 'partial':
    case 'none':
      return false;
    default:
      return assertNever(coverage);
  }
};

export const coversNothing = (coverage: Coverage): boolean => {
  switch (coverage) {
    case 'none':
      return true;
    case 'full':
    case 'partial':
      return false;
    default:
      return assertNever(coverage);
  }
};

const groupsWithGaps = (alerts: LicenseAlerts): ReadonlyArray<LicenseGroup> =>
  alerts.licenseGroups.filter((group) => !coversEverything(group.coverage));

export const participantsWithGaps = (alerts: LicenseAlerts): number =>
  groupsWithGaps(alerts).reduce((total, group) => total + group.participants.length, 0);

export const totalParticipants = (alerts: LicenseAlerts): number =>
  alerts.licenseGroups.reduce((total, group) => total + group.participants.length, 0);

export const totalCourses = (alerts: LicenseAlerts): number =>
  alerts.collection.programFamilies.length;

export const enrollableCourseCount = (alerts: LicenseAlerts, group: LicenseGroup): number =>
  totalCourses(alerts) - group.hiddenProgramFamilyIds.length;

export const coursesNeedingALicense = (
  alerts: LicenseAlerts,
  group: LicenseGroup,
): ReadonlyArray<ProgramFamily> =>
  alerts.collection.programFamilies.filter((programFamily) =>
    group.hiddenProgramFamilyIds.includes(programFamily.id),
  );

// Licenses gate enrolment, so "everyone" below always means everyone who was
// added, whether or not they end up receiving an invitation.
//
// 'clear'   - everyone can enrol in every course
// 'mixed'   - some can, some cannot
// 'limited' - nobody can enrol in everything, but everybody can enrol in something
// 'blocked' - nobody can enrol in anything
export type AlertSeverity = 'clear' | 'mixed' | 'limited' | 'blocked';

export const severityOf = (alerts: LicenseAlerts, unrecognizedCount: number): AlertSeverity => {
  if (peopleWithoutFullAccess(alerts, unrecognizedCount) === 0) {
    return 'clear';
  }

  if (alerts.licenseGroups.some((group) => coversEverything(group.coverage))) {
    return 'mixed';
  }

  // An empty group list satisfies every(), which is right: if the only people
  // in the list are outside the organization, nobody can enroll in anything.
  return alerts.licenseGroups.every((group) => coversNothing(group.coverage))
    ? 'blocked'
    : 'limited';
};

// The three tabs.
export type AccessLevel = 'full' | 'partial' | 'none';

export const ALL_ACCESS_LEVELS: ReadonlyArray<AccessLevel> = ['full', 'partial', 'none'];

export const accessLevelOf = (coverage: Coverage): AccessLevel => coverage;

export const groupsAt = (alerts: LicenseAlerts, level: AccessLevel): ReadonlyArray<LicenseGroup> =>
  alerts.licenseGroups.filter((group) => group.coverage === level);

export const participantsAt = (alerts: LicenseAlerts, level: AccessLevel): number =>
  groupsAt(alerts, level).reduce((total, group) => total + group.participants.length, 0);

// Someone outside the organization cannot enrol in anything, so they count with
// the unlicensed rather than sitting outside every total.
export const participantsAtIncludingOutsiders = (
  alerts: LicenseAlerts,
  level: AccessLevel,
  unrecognizedCount: number,
): number => participantsAt(alerts, level) + (level === 'none' ? unrecognizedCount : 0);

const gapParticipants = (alerts: LicenseAlerts) =>
  alerts.licenseGroups
    .filter((group) => !coversEverything(group.coverage))
    .flatMap((group) => [...group.participants]);

export const unlicensedParticipantsWithGaps = (alerts: LicenseAlerts): number =>
  gapParticipants(alerts).filter((p) => p.licenseLabel.isNothing()).length;

// Only the ids the request will actually carry. Holding a licence is not
// enough: the directory lookup rejects an address whose domain is not
// registered to the organization, so that person never reaches verifiedUserIds
// however many licences they hold.
export const invitedParticipantsWithGaps = (
  alerts: LicenseAlerts,
  invitedUserIds: ReadonlyArray<number>,
): number => gapParticipants(alerts).filter((p) => invitedUserIds.includes(p.id)).length;

export const peopleWithoutFullAccess = (alerts: LicenseAlerts, unrecognizedCount: number): number =>
  participantsWithGaps(alerts) + unrecognizedCount;

// Who the invite will actually reach today. Addresses outside the organization
// never resolve to a person, and participants holding no license are filtered
// out of the payload before it is sent (see DVLP-7735).
export const invitationCount = (alerts: LicenseAlerts): number =>
  alerts.licenseGroups
    .filter((group) => group.primaryLicenseLabel.isJust())
    .reduce((total, group) => total + group.participants.length, 0);

export const unlicensedParticipantCount = (alerts: LicenseAlerts): number =>
  alerts.licenseGroups
    .filter((group) => group.primaryLicenseLabel.isNothing())
    .reduce((total, group) => total + group.participants.length, 0);

// Everyone the admin added, including the addresses that matched nobody.
export const peopleInList = (alerts: LicenseAlerts, unrecognizedCount: number): number =>
  totalParticipants(alerts) + unrecognizedCount;

// Whether some of a group's access only begins once a later license starts
// (DVLP-8068). A license starting after the collection ends gives nothing, so
// those people are not "starting later"; they are unlicensed here.
export const startsLater = (accessStarts: AccessStarts): boolean => {
  switch (accessStarts) {
    case 'later':
    case 'partly-later':
      return true;
    case 'now':
    case 'after-collection-end':
      return false;
    default:
      return assertNever(accessStarts);
  }
};

const groupsStartingLater = (alerts: LicenseAlerts): ReadonlyArray<LicenseGroup> =>
  alerts.licenseGroups.filter((group) => startsLater(group.accessStarts));

export const participantsStartingLater = (alerts: LicenseAlerts): number =>
  groupsStartingLater(alerts).reduce((total, group) => total + group.participants.length, 0);

export interface StartingLater {
  firstName: string;
  startsOn: Date;
}

// The one person whose license starts later, when there is exactly one, so
// the banner can name them and the day.
export const soleParticipantStartingLater = (alerts: LicenseAlerts): Maybe<StartingLater> =>
  just(
    groupsStartingLater(alerts).flatMap((group) =>
      group.participants.map(({ firstName }) => ({ firstName, startsOn: group.startsOn })),
    ),
  )
    .andThen(
      (people): Maybe<{ firstName: string; startsOn: Maybe<Date> }> =>
        people.length === 1 ? just(people[0]) : nothing(),
    )
    .andThen(({ firstName, startsOn }) => startsOn.map((date) => ({ firstName, startsOn: date })));

// At one person the copy speaks about them by name rather than counting.
export const soleParticipant = (
  alerts: LicenseAlerts,
  unrecognizedCount: number,
): Maybe<GroupParticipant> => {
  if (peopleInList(alerts, unrecognizedCount) !== 1 || unrecognizedCount > 0) {
    return nothing();
  }

  return alerts.licenseGroups
    .flatMap((group) => [...group.participants])
    .reduce<
      Maybe<GroupParticipant>
    >((found, participant) => found.orElse(() => Maybe.just(participant)), nothing());
};
